import { ButtonSize } from '../../Framework/Data/Enums/Generic';
import { ColourMod, FlexAlign, FlexGap, FlexJustify } from '../../Framework/Data/Enums/LayoutEnums';
import { UploadFile } from '../../Framework/Components/Forms/File/UploadFile';
import Button, { ButtonSubmit } from '../../Framework/Components/Buttons/Button';
import FormEntryWrapper, { FormWrapperLabel } from '../../Framework/Components/Forms/FormEntryWrapper/FormEntryWrapper';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { AddressListItemType, AddressType } from '../../Types/AddressType';
import { Controller, FieldValues, FormProvider, useForm, useFormContext } from 'react-hook-form';
import { AddAddressModal } from '../AddAddress';
import { SendsAddressType, updateSendsPageAddress, useUpdateSendsDetail } from '../../Api/StandaloneSendsEndpoints';
import {
	FileResponseType,
	maxFileSizeInBytes,
	maxPrivilegedFileSizeInBytes,
	sendMail,
	uploadFile,
	uploadFilesForMerge,
} from '../../Api/SendMailEndpoints';
import AlertBanner from '../../Framework/Components/AlertBanner/AlertBanner';
import SendProcessingWizardModal from './Modals/SendProcessingWizardModal';
import { useSendMail } from '../../Providers/SendMailProvider';
import SelectFilter from '../../Framework/Components/Forms/SelectFilter/SelectFilter';
import StackVertical from '../../Framework/Components/Wrappers/StackVertical';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBrakeWarning, faPaperPlane, faPencil, faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import ImportCsvModal from '../AddressImporter/Modals/ImportCsvModal';
import ImportVcfModal from '../AddressImporter/Modals/ImportVcfModal';
import { NavLink, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import SendMultipleProcessingWizardModal from './Modals/SendMultipleProcessingWizardModal';
import StackHorizontal from '../../Framework/Components/Wrappers/StackHorizontal';
import { useUser } from '../../Api/UsersEndpoints';

export type SendDataProcess = {
	form: any;
	address?: string;
	postage?: string;

	sendProcessOpen?: boolean;

	uploadFileActive?: boolean;
	sendDataActive?: boolean;

	postingData?: boolean;
	sendComplete?: boolean;
	trackingId?: any;
	pageCount?: number;
};

const StandaloneSendForm = () => {
	const { data } = useUser();
	const { pageId } = useParams();

	const queryClient = useQueryClient();

	const {
		address,
		postage,
		reset,
		sendPageData,
		sendPageLoading,
		sendPageError,
		sendPageID,
		UpdateAddress,
		ResetSendMail,
	} = useSendMail();

	const formMethods = useForm({
		defaultValues: {
			address: address?.id || -1,
			postage: postage?.id || -1,
			file: null,
			clientReferenceId: '',
			notes: '',
		},
	});

	const [sendError, setSendError] = useState<Record<string, string[]> | string | null>(null);
	const [addAddressActive, setAddAddressActive] = useState<boolean>(false);

	const [importCsvOpen, setImportCsvOpen] = useState<boolean>(false);
	const [importVcfOpen, setImportVcfOpen] = useState<boolean>(false);
	const [usingMergeMode, setUsingMergeMode] = useState<boolean>(false);
	const [uploadError, setUploadError] = useState<boolean>(false);

	const [fileCount, setFileCount] = useState<number>(0);

	const [sendProcessData, setSendProcessData] = useState<SendDataProcess>({
		form: {
			address: address?.id || -1,
			postage: postage?.id || -1,
			file: null,
			clientReferenceId: '',
			notes: '',
		},
		address: address?.value,
		postage: postage?.value,

		sendProcessOpen: false,

		uploadFileActive: false,
		sendDataActive: false,

		postingData: false,

		sendComplete: false,
	});

	const {
		control,
		handleSubmit,
		register,
		reset: formReset,
		formState: { isValid, errors },
	} = formMethods;

	const sendsTemplates = useUpdateSendsDetail();

	const HandleUpload = (data: FieldValues) => {
		if (data.file.length === 1) {
			uploadFile(data.file[0])
				.then((r: FileResponseType) => {
					setSendProcessData((prevState) => ({
						...prevState,
						uploadFileActive: false,
						sendDataActive: true,
						postingData: true,
					}));

					HandleSend(r.fileUuid, { ...data, sendsPageId: sendPageID });
				})
				.catch((e) => {
					setSendError(e.response.data.error ?? e.response.data ?? e.toString());
					setSendProcessData((prevState) => ({
						...prevState,
						sendProcessOpen: false,
						uploadFileActive: false,
						sendDataActive: false,
						postingData: false,
					}));
				});
		} else {
			uploadFilesForMerge(data.file)
				.then((r: FileResponseType) => {
					setSendProcessData((prevState) => ({
						...prevState,
						uploadFileActive: false,
						sendDataActive: true,
						postingData: true,
					}));
					HandleSend(r.fileUuid, { ...data, sendsPageId: sendPageID });
				})
				.catch((e) => {
					setSendError(e.response.data.error ?? e.response.data ?? e.toString());
					setSendProcessData((prevState) => ({
						...prevState,
						sendProcessOpen: false,
						uploadFileActive: false,
						sendDataActive: false,
						postingData: false,
					}));
				});
		}
	};

	const HandleProcessStart = (data: FieldValues) => {
		setSendError(null);

		if (usingMergeMode) {
			setSendProcessData((prevState) => ({
				...prevState,
				form: { ...data, sendsPageId: sendPageID },
				address: address?.value,
				postage: postage?.value,
				sendProcessOpen: true,
				uploadFileActive: true,
				postingData: true,
			}));

			HandleUpload(data);
		} else {
			setSendProcessData((prevState) => ({
				...prevState,
				form: { ...data, sendsPageId: sendPageID, notes: '', clientReferenceId: '' },
				address: address?.value,
				postage: postage?.value,
				sendProcessOpen: true,
			}));
		}
	};

	const HandleSend = (fileUuid: string, data: FieldValues) => {
		sendMail({ ...data, file: fileUuid })
			.then((s) => {
				setSendProcessData((prevState) => ({
					...prevState,
					sendDataActive: false,
					sendComplete: true,
					postingData: true,
					trackingId: s.trackingId,
					pageCount: s.pageCount,
				}));
				queryClient.invalidateQueries(['allSendsHistory']).then(() => null);
			})
			.catch((e) => {
				setSendError(e.toString());
				setSendProcessData((prevState) => ({
					...prevState,
					sendProcessOpen: false,
					uploadFileActive: false,
					sendDataActive: false,
					postingData: false,
				}));
			});
	};

	const HandleNewAddress = useCallback(
		async (singleAddress: AddressType) => {
			setAddAddressActive(false);

			const newPostageArray: Array<number> = [];
			sendPageData.addresses[0].postageOptions.forEach((postage: any) => newPostageArray.push(postage.id));

			const patchAddresses = [{ id: singleAddress.addressId, postageOptions: newPostageArray }];
			sendPageData.addresses.forEach((address: any) =>
				patchAddresses.push({
					id: address.id,
					postageOptions: address?.postageOptions.map((postage: any) => postage.id),
				})
			);

			const newAddressUiData = [
				...sendPageData.addresses,
				{
					value: `${singleAddress.name}, ${singleAddress.addressLine1}, ${singleAddress.addressLine2}, ${singleAddress.postCode}, ${singleAddress.countryCode}`,
					id: singleAddress.addressId,
					postageOptions: [...sendPageData.addresses[0].postageOptions],
				},
			];

			await ForwardAddressChangeData(patchAddresses, newAddressUiData, singleAddress.addressId, 1);
		},
		[sendPageData]
	);

	const HandleNewBulkAddresses = useCallback(
		async (bulkAddresses: Array<AddressListItemType>) => {
			setAddAddressActive(false);

			const newPostageArray: Array<number> = [];
			sendPageData.addresses[0].postageOptions.forEach((postage: any) => newPostageArray.push(postage.id));

			const patchAddresses: Array<any> = [];
			sendPageData.addresses.forEach((address: any) =>
				patchAddresses.push({
					id: address.id,
					postageOptions: address?.postageOptions.map((postage: any) => postage.id),
				})
			);

			const newBulkAddressesUiData: Array<SendsAddressType> = [];

			bulkAddresses.forEach((item: AddressListItemType) => {
				patchAddresses.push({
					id: item.value,
					postageOptions: newPostageArray,
				});

				newBulkAddressesUiData.push({
					id: item.value,
					value: item.label,
					postageOptions: [...sendPageData.addresses[0].postageOptions],
				});
			});

			const newAddressUiData = [...sendPageData.addresses, ...newBulkAddressesUiData];

			if (bulkAddresses.length === 0) {
				setImportCsvOpen(false);
				setImportVcfOpen(false);
			} else {
				await ForwardAddressChangeData(
					patchAddresses,
					newAddressUiData,
					bulkAddresses[0].value,
					bulkAddresses.length
				);
			}
		},
		[sendPageData]
	);

	const ForwardAddressChangeData = async (patchData: any, uiData: any, nextValue: number, addressCount: number) => {
		await updateSendsPageAddress(sendPageData.id as number, patchData)
			.then((resp: any) => {
				sendsTemplates
					.mutateAsync({
						newData: { ...sendPageData, addresses: uiData },
					})
					.then(() => {
						UpdateAddress(nextValue, true);
					});
			})
			.catch((e: any) => console.error(e))
			.finally(() => {
				setImportCsvOpen(false);
				setImportVcfOpen(false);
			});
	};

	const ResetSend = () => {
		formReset();
		ResetSendMail();
		setSendError(null);
		setSendProcessData({
			address: address?.value,
			postage: postage?.value,
			form: {
				address: address?.id || -1,
				postage: postage?.id || -1,
				file: null,
				clientReferenceId: '',
				notes: '',
			},

			sendProcessOpen: false,

			uploadFileActive: false,
			sendDataActive: false,

			postingData: false,
			sendComplete: false,
		});

		if (sendPageData && sendPageData.addresses.length === 1 && sendPageData.addresses[0].id === 100) {
			formMethods.setValue('address', sendPageData.addresses[0].id);
		}
	};

	useEffect(() => {
		if (pageId) {
			ResetSend();
		}
	}, [pageId]);

	useEffect(() => {
		document.title = sendPageData?.title
			? `${sendPageData.title} | UK Postbox`
			: 'Loading Sends Profile | UK Postbox';
		if (sendPageData && sendPageData.addresses.length === 1 && sendPageData.addresses[0].id === 100) {
			formMethods.setValue('address', sendPageData.addresses[0].id);
		}
	}, [sendPageData]);

	return (
		<FormProvider {...formMethods}>
			<div className={'sticky sends-sticky'}>
				{sendPageLoading ? (
					<div className={'content-loader content-fader'}>
						<div style={{ height: '53.5em' }} />
					</div>
				) : sendPageError ? (
					<div className={'empty-state-container'}>
						<StackVertical
							gap={FlexGap.ExtraSmall}
							align={FlexAlign.Center}
							justify={FlexJustify.Center}
							extraClass={'p-xxl text-center'}
						>
							<FontAwesomeIcon icon={faTriangleExclamation} size={'3x'} />
							<h6 className={'sub-heading mt-sm mb-xs'}>Error Loading Page: {pageId}</h6>
							<p>
								The requested page could not be found, or is no longer available. If you believe this to
								be incorrect, please contact the account owner.
							</p>
						</StackVertical>
					</div>
				) : (
					<>
						{sendPageData.isActive && (
							<div className={'card user-sends-page-surface'}>
								<div className={'card-body'}>
									<form onSubmit={handleSubmit((data, e) => HandleProcessStart(data))}>
										<fieldset>
											<StackHorizontal justify={FlexJustify.Between}>
												<div>
													<h4 className={'mb-0'}>
														{sendPageData.title ? sendPageData.title : 'Title'}
													</h4>
													<p className={'m-0'}>
														{sendPageData.description ? sendPageData.description : ''}
													</p>
												</div>

												{data?.roles.includes('SENDS_PAGE_ADMIN') && (
													<NavLink
														to={`../sends-admin/edit/${sendPageData.id}`}
														className={'btn btn-icon'}
													>
														<FontAwesomeIcon icon={faPencil} />
														Edit
													</NavLink>
												)}
											</StackHorizontal>

											<hr />

											<div className={'flex-column flex-gap-md'}>
												{sendPageData.addresses[0]?.id === 100 ? (
													<div>
														<label className={'mb-xs'}>Address</label>
														<AlertBanner
															icon={<FontAwesomeIcon icon={faBrakeWarning} size={'lg'} />}
															title={'Using Envelope Window'}
															content={
																'Ensure the destination address is on the first page of your selected PDFs.'
															}
														/>
													</div>
												) : (
													<StackVertical gap={FlexGap.ExtraSmall}>
														<AddressControl
															addAddressCallback={() => setAddAddressActive(true)}
														/>

														{sendPageData.allowCreateAddress && (
															<div className={'row flex-gap-sm'}>
																<div className={'col-xs-12 col-sm-6'}>
																	<Button
																		title={'New Address'}
																		fullWidth={true}
																		size={ButtonSize.Small}
																		onClick={() => setAddAddressActive(true)}
																	/>
																</div>
																<div className={'col-xs-12 col-sm-6'}>
																	<Button
																		title={'Import from CSV'}
																		fullWidth={true}
																		size={ButtonSize.Small}
																		onClick={() => setImportCsvOpen(true)}
																	/>
																</div>
																{/*<div className={'col-xs-12 col-sm-6 col-md-4'}>
																	<Button
																		title={'Import from vCard'}
																		fullWidth={true}
																		size={ButtonSize.Small}
																		onClick={() => setImportVcfOpen(true)}
																	/>
																</div>*/}
															</div>
														)}
													</StackVertical>
												)}

												<PostageControl />

												<Controller
													control={control}
													name='file'
													rules={{ required: 'A file is required' }}
													render={({ field, fieldState }) => (
														<UploadFile
															name={'file upload'}
															onChange={(e: any) => {
																setFileCount(e?.length || 0);
																field.onChange(e);
															}}
															onError={(errorState) => setUploadError(errorState)}
															acceptsFileExtension={['.pdf']}
															extensionDisplayValue={'PDF'}
															reset={reset}
															useBulkMode={sendPageData.addresses[0]?.id === 100}
															complexInteractions
															hideFileExtension
															onModeChange={(state) => setUsingMergeMode(state)}
															maxFiles={10}
															maxSizeInBytes={
																data?.permissions?.includes(
																	'Client_AllowSendsProfilesLargestFileUpload'
																)
																	? maxPrivilegedFileSizeInBytes
																	: maxFileSizeInBytes
															}
														/>
													)}
												/>

												{usingMergeMode && (
													<>
														<Controller
															control={control}
															name='clientReferenceId'
															rules={{
																maxLength: {
																	value: 300,
																	message: 'Too many characters entered (300 max)',
																},
															}}
															render={({ field, fieldState }) => (
																<FormEntryWrapper invalid={fieldState.error?.message}>
																	<FormWrapperLabel
																		label={'My Reference Id'}
																		validationMessage={fieldState.error?.message}
																	>
																		<input
																			aria-invalid={
																				fieldState.error?.message
																					? 'true'
																					: 'false'
																			}
																			onChange={(e: any) => field.onChange(e)}
																		/>
																	</FormWrapperLabel>
																</FormEntryWrapper>
															)}
														/>

														<Controller
															control={control}
															name='notes'
															rules={{
																maxLength: {
																	value: 4000,
																	message: 'Too many characters entered (4000 max)',
																},
															}}
															render={({ field, fieldState }) => (
																<FormEntryWrapper invalid={fieldState.error?.message}>
																	<FormWrapperLabel
																		label={'Notes'}
																		validationMessage={fieldState.error?.message}
																	>
																		<textarea
																			rows={6}
																			onChange={(e: any) => field.onChange(e)}
																		></textarea>
																	</FormWrapperLabel>
																</FormEntryWrapper>
															)}
														/>
													</>
												)}

												<div className={'sticky sticky-bottom pt-sm pb-sm'}>
													<StackVertical gap={FlexGap.Small}>
														{sendError && (
															<AlertBanner
																title={typeof sendError === 'object' ? '' : sendError}
																content={
																	typeof sendError === 'object' ? (
																		<StackVertical gap={FlexGap.ExtraSmall}>
																			{Object.keys(sendError).map(
																				(error, index) => (
																					<div>
																						<strong>{error}</strong>
																						{sendError[error].map(
																							(message) => (
																								<div>{message}</div>
																							)
																						)}
																					</div>
																				)
																			)}
																		</StackVertical>
																	) : undefined
																}
																colourMod={ColourMod.Alert}
															/>
														)}
														<ButtonSubmit
															loading={sendProcessData.sendProcessOpen}
															title={
																usingMergeMode && fileCount > 1
																	? 'Merge & Send'
																	: 'Send'
															}
															colour={ColourMod.Primary}
															fullWidth={true}
															disabled={!isValid || uploadError}
														/>
													</StackVertical>
												</div>
											</div>
										</fieldset>
									</form>
								</div>
							</div>
						)}

						{!sendPageData.isActive && (
							<StackVertical
								gap={FlexGap.ExtraSmall}
								align={FlexAlign.Center}
								justify={FlexJustify.Center}
								extraClass={'m-xxl p-xxl text-center'}
							>
								<FontAwesomeIcon icon={faPaperPlane} size={'3x'} />
								<h6 className={'sub-heading mt-sm mb-xs'}>Page No Longer Active</h6>
								<p>If you believe this is an error, please contact your sends administrator</p>
							</StackVertical>
						)}
					</>
				)}

				{usingMergeMode && (
					<SendProcessingWizardModal
						isOpen={sendProcessData.sendProcessOpen}
						sendData={sendProcessData}
						closeCallback={() =>
							sendProcessData.postingData
								? ResetSend()
								: setSendProcessData((prevState) => ({
										...prevState,
										sendProcessOpen: false,
								  }))
						}
						lockOpen={sendProcessData.postingData}
					/>
				)}

				{sendProcessData.sendProcessOpen && !usingMergeMode && (
					<SendMultipleProcessingWizardModal
						isOpen={sendProcessData.sendProcessOpen}
						sendData={sendProcessData}
						closeCallback={() => ResetSend()}
						lockOpen={sendProcessData.postingData}
					/>
				)}

				{addAddressActive && (
					<AddAddressModal
						visible={addAddressActive}
						closeCallback={() => setAddAddressActive(false)}
						actionedCallback={(address: AddressType) => HandleNewAddress(address)}
						existingData={{
							phoneNo: sendPageData.defaultPhoneNumber,
							emailAddress: sendPageData.defaultEmailAddress,
						}}
					/>
				)}

				{importCsvOpen && (
					<ImportCsvModal
						visible={importCsvOpen}
						closeCallback={() => setImportCsvOpen(false)}
						actionedCallback={(addresses: Array<AddressListItemType>) => HandleNewBulkAddresses(addresses)}
					/>
				)}

				{importVcfOpen && (
					<ImportVcfModal
						visible={importVcfOpen}
						closeCallback={() => setImportVcfOpen(false)}
						actionedCallback={(addresses: Array<AddressListItemType>) => HandleNewBulkAddresses(addresses)}
					/>
				)}
			</div>
		</FormProvider>
	);
};

export default StandaloneSendForm;

type AddressControlType = {
	addAddressCallback: any;
};

const AddressControl = memo((props: AddressControlType) => {
	const { control, setValue } = useFormContext();
	const { reset, address, addressOptions, sendPageID, UpdateAddress } = useSendMail();

	const UpdateSelectedAddress = useCallback(() => {
		if (address) {
			setValue('address', address.id);
		} else if (addressOptions && addressOptions.length === 1) {
			setValue('address', addressOptions[0].value);
		} else {
			setValue('address', -1);
		}
	}, [addressOptions]);

	useEffect(() => {
		UpdateSelectedAddress();
	}, [reset, sendPageID, addressOptions]);

	return (
		<Controller
			control={control}
			name='address'
			rules={{ required: true, validate: (value) => (value === -1 ? 'Address Selection is required' : true) }}
			render={({ field, fieldState }) => (
				<FormWrapperLabel label={'Address'} validationMessage={fieldState.error?.message}>
					{addressOptions.length > 1 ? (
						<SelectFilter
							label={'Address'}
							name={'address'}
							placeholder={'Select Address...'}
							onBlur={field.onBlur}
							onChange={(e: any) => {
								field.onChange(e);
								UpdateAddress(e);
							}}
							defaultValue={[field.value]}
							data={addressOptions}
							error={fieldState.error}
							minimal={true}
						/>
					) : (
						<input
							readOnly={true}
							value={addressOptions.length === 0 ? 'Use Envelope Window' : addressOptions[0].label}
						/>
					)}
				</FormWrapperLabel>
			)}
		/>
	);
});

const PostageControl = memo(() => {
	const { control, setValue } = useFormContext();

	const { postageOptions, reset, address, addressOptions, UpdatePostage, sendPageID } = useSendMail();

	const UpdateSelectedPostage = () => {
		if (postageOptions && postageOptions.length === 1) {
			setValue('postage', postageOptions[0].value);
		} else {
			setValue('postage', -1);
		}
	};

	useEffect(() => {
		UpdateSelectedPostage();
	}, [reset, address, sendPageID, addressOptions, postageOptions]);

	return (
		<Controller
			control={control}
			name='postage'
			rules={{
				required: 'Postage Option is required',
				validate: (value) => (value === -1 ? 'Postage Option is required' : true),
			}}
			render={({ field, fieldState }) => (
				<FormWrapperLabel label={'Postage'} validationMessage={fieldState.error?.message}>
					{postageOptions.length > 1 ? (
						<SelectFilter
							label={'Postage'}
							name={'postage'}
							placeholder={'Select Postage...'}
							onBlur={field.onBlur}
							onChange={(e: any) => {
								field.onChange(e);
								UpdatePostage(e);
							}}
							defaultValue={[field.value]}
							data={postageOptions}
							error={fieldState.error}
							minimal={true}
						/>
					) : (
						<input
							readOnly={true}
							value={postageOptions.length === 0 ? '' : postageOptions[0].label}
							disabled={postageOptions.length === 0}
							placeholder={'Select Postage...'}
						/>
					)}
				</FormWrapperLabel>
			)}
		/>
	);
});
